import { useEffect, useRef, useState, useMemo } from "react";
import { VirtuosoHandle } from "react-virtuoso";
import {
  GroupedResult,
  GroupingConfig,
  ScrollManagerConfig,
  RenderContext,
} from "./types";

interface UseVirtuosoWrapperStateProps<T> {
  data: T[];
  grouping?: GroupingConfig<T>;
  scrollManager?: ScrollManagerConfig<T>;
  isLoadingMore?: boolean;
}

interface UseVirtuosoWrapperStateReturn<T> {
  /** Ref to attach to Virtuoso component */
  virtuosoRef: React.RefObject<VirtuosoHandle>;

  /** Current first item index (for scroll preservation) */
  firstItemIndex: number;

  /** Whether user is at the top of the list */
  atListTop: boolean;

  /** Callback for Virtuoso's atTopStateChange */
  handleAtTopChange: (atTop: boolean) => void;

  /** Whether to show "new items available" notification */
  showUpdateNotification: boolean;

  /** Function to scroll to top (smooth scroll) */
  scrollToTop: () => void;

  /** The grouped result (or flat result if no grouping) */
  groupedData: GroupedResult<T>;

  /** Whether this is a grouped list */
  isGrouped: boolean;

  /** Helper function to get render context for an item */
  getRenderContext: (index: number) => RenderContext;
}

/**
 * Internal hook for managing grouped virtual list state
 * Handles scroll preservation, new items notification, and grouping logic
 */
export function useVirtuosoWrapperState<T>({
  data,
  grouping,
  scrollManager,
  isLoadingMore = false,
}: UseVirtuosoWrapperStateProps<T>): UseVirtuosoWrapperStateReturn<T> {
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Extract scroll config values to avoid creating new objects every render
  const scrollEnabled = scrollManager?.enabled ?? false;
  const scrollStartingIndex = scrollManager?.startingIndex ?? 9999;
  const compareFirstItem = useMemo(
    () =>
      scrollManager?.compareFirstItem ??
      ((a: T | undefined, b: T | undefined) => a === b),
    [scrollManager?.compareFirstItem]
  );

  // IMPORTANT: Only use custom firstItemIndex if scrollManager is explicitly enabled
  // Initialize with the correct value based on initial props
  // The useState callback ensures correct initialization without race conditions
  const [firstItemIndex, setFirstItemIndex] = useState(() => {
    return scrollEnabled ? scrollStartingIndex : 0;
  });

  const [atListTop, setAtListTop] = useState(true);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  // Determine if this is a grouped list
  const isGrouped = !!grouping;

  // Apply grouping if configured
  const groupedData = useMemo<GroupedResult<T>>(() => {
    if (grouping) {
      const result = grouping.groupBy(data);

      // Validate the grouped result
      const totalCount = result.groupCounts.reduce(
        (sum, count) => sum + count,
        0
      );
      if (totalCount !== result.items.length) {
        console.error(
          "useVirtuosoWrapperState: Mismatch between groupCounts total and items length:",
          totalCount,
          result.items.length
        );
      }

      return result;
    }

    // No grouping - return flat structure
    return {
      groups: [],
      groupCounts: [],
      items: data,
      itemToGroupMap: [],
    };
  }, [data, grouping]);

  // Track previous item count and first item for detecting prepends (synchronous pattern)
  // Must be declared after groupedData memo so initializers can reference it
  const [prevItemCount, setPrevItemCount] = useState(
    () => groupedData.items.length
  );
  const [prevFirstItem, setPrevFirstItem] = useState<T | undefined>(
    () => groupedData.items[0]
  );

  // Scroll to top function
  const scrollToTop = () => {
    virtuosoRef.current?.scrollToIndex({
      index: 0,
      align: "start",
      behavior: "smooth",
    });
  };

  // Handle at top state changes from Virtuoso
  const handleAtTopChange = (atTop: boolean) => {
    setAtListTop(atTop);
  };

  // Get render context for an item
  const getRenderContext = (index: number): RenderContext => {
    if (!isGrouped || !groupedData.itemToGroupMap) {
      return {};
    }

    const groupIndex = groupedData.itemToGroupMap[index];
    const groupName = groupedData.groups[groupIndex];

    // Find first and last item indices in this group
    let groupStartIndex = 0;
    for (let i = 0; i < groupIndex; i++) {
      groupStartIndex += groupedData.groupCounts[i];
    }
    const groupEndIndex = groupStartIndex + groupedData.groupCounts[groupIndex];

    return {
      groupIndex,
      groupName,
      isFirstInGroup: index === groupStartIndex,
      isLastInGroup: index === groupEndIndex - 1,
    };
  };

  // Synchronous detection of prepended items during render.
  // Uses React's "store information from previous renders" pattern so that
  // firstItemIndex and data change in the SAME render cycle — eliminating
  // the one-frame flash that useEffect would cause.
  if (scrollEnabled) {
    const currentCount = groupedData.items.length;
    const currentFirstItem = groupedData.items[0];

    if (
      currentCount !== prevItemCount ||
      !compareFirstItem(currentFirstItem, prevFirstItem)
    ) {
      const diff = currentCount - prevItemCount;
      const firstItemChanged = !compareFirstItem(
        currentFirstItem,
        prevFirstItem
      );

      if (diff > 0 && firstItemChanged && !atListTop && !isLoadingMore) {
        setFirstItemIndex((prev) => prev - diff);
        setShowUpdateNotification(true);
      }

      setPrevItemCount(currentCount);
      setPrevFirstItem(currentFirstItem);
    }
  }

  // Hide notification when user scrolls to top
  useEffect(() => {
    if (atListTop) {
      setShowUpdateNotification(false);
    }
  }, [atListTop]);

  return {
    virtuosoRef,
    firstItemIndex,
    atListTop,
    handleAtTopChange,
    showUpdateNotification,
    scrollToTop,
    groupedData,
    isGrouped,
    getRenderContext,
  };
}
